import leven from 'leven';

/**
 * AI generated scoring of company names.
 */
export type ScoredItem<T> = {
  item: T;
  score: number;
  comparedValue: string;
  normalizedQuery: string;
  normalizedComparedValue: string;
};

export type ScoreOptions<T> = {
  /** How to get the string from an object to compare against the query */
  getValue: (item: T) => string;

  /** Minimum score to include in the returned list (default: 0) */
  minScore?: number;

  /** If true, containment is symmetric: max(cont(q->c), cont(c->q)) */
  symmetricContainment?: boolean;

  /** Return only top N items (default: no limit) */
  topN?: number;
};

// --- normalization / tokenization (Germany-friendly) ---

const MULTIWORD_LEGAL_FORMS = [
  'gmbh & co kg',
  'gmbh & co. kg',
  'gmbh und co kg',
  'gmbh und co. kg',
  'gmbh u co kg',
  'gmbh u. co. kg',
  'ag & co kg',
  'ag & co. kg',
  'ug (haftungsbeschraenkt)',
  'ug (haftungsbeschränkt)',
];

const SINGLE_LEGAL_FORMS = new Set([
  'gmbh',
  'mbh',
  'ug',
  'ag',
  'kg',
  'ohg',
  'gbr',
  'se',
  'eg',
  'kgaa',
  'partg',
  'partgmbb',
  'ek',
  'e.k',
  'ev',
  'e.v',
]);

const STOPWORDS = new Set(['&', 'und', '+', 'u', 'u.', 'and']);

function escapeRegex(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function normalizeBase(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[’´`]/g, "'")
    .replace(/[,./()'"“”„\-_:;]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function stripMultiwordLegalForms(s: string): string {
  let out = ` ${s} `;
  for (const lf of MULTIWORD_LEGAL_FORMS.sort((a, b) => b.length - a.length)) {
    const re = new RegExp(`\\b${escapeRegex(normalizeBase(lf))}\\b`, 'g');
    out = out.replace(re, ' ');
  }
  return out.replace(/\s+/g, ' ').trim();
}

function tokenizeCompany(raw: string): string[] {
  let s = normalizeBase(raw);
  s = stripMultiwordLegalForms(s);

  return s
    .split(' ')
    .map((t) => t.replace(/\./g, '')) // "e.k." -> "ek"
    .filter(Boolean)
    .filter((t) => !STOPWORDS.has(t))
    .filter((t) => !SINGLE_LEGAL_FORMS.has(t));
}

// --- scoring ---

function jaccard(a: string[], b: string[]): number {
  const A = new Set(a);
  const B = new Set(b);
  if (A.size === 0 && B.size === 0) return 1;
  const inter = [...A].filter((x) => B.has(x)).length;
  const union = new Set([...A, ...B]).size;
  return union === 0 ? 0 : inter / union;
}

function containment(query: string[], cand: string[]): number {
  const Q = new Set(query);
  const C = new Set(cand);
  if (Q.size === 0) return 0;
  let hit = 0;
  for (const t of Q) if (C.has(t)) hit++;
  return hit / Q.size;
}

function levSimilarity(a: string, b: string): number {
  if (a === b) return 1;
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  return 1 - leven(a, b) / maxLen;
}

/**
 * Scores a list of objects against a query string.
 *
 * - Compares only one configured value per item (via getValue)
 * - Returns the scored list (sorted by score desc)
 */
export function scoreCompanyNameMatches<T>(query: string, items: T[], options: ScoreOptions<T>): ScoredItem<T>[] {
  const minScore = options.minScore ?? 0;
  const topN = options.topN ?? Number.POSITIVE_INFINITY;
  const symmetricContainment = options.symmetricContainment ?? true;

  const qTokens = tokenizeCompany(query);
  const normalizedQuery = qTokens.join(' ');

  const scored: ScoredItem<T>[] = items.map((item) => {
    const comparedValue = options.getValue(item) ?? '';
    const cTokens = tokenizeCompany(comparedValue);

    const contQC = containment(qTokens, cTokens);
    const contCQ = containment(cTokens, qTokens);
    const cont = symmetricContainment ? Math.max(contQC, contCQ) : contQC;

    const jac = jaccard(qTokens, cTokens);
    const lev = levSimilarity(normalizedQuery, cTokens.join(' '));

    // tuned for extra tokens + typos
    const score = 0.55 * cont + 0.3 * jac + 0.15 * lev;

    return {
      item,
      score,
      comparedValue,
      normalizedQuery,
      normalizedComparedValue: cTokens.join(' '),
    };
  });

  return scored
    .filter((s) => s.score >= minScore)
    .sort((a, b) => b.score - a.score)
    .slice(0, topN);
}
